import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSignupApi } from '../api';
import { errorMessage, errorStatus, isTransientFailure } from '../helpers/apiErrors';
import { clearSignupToken, collectAttribution, getSignupToken, setSignupToken } from '../helpers/signupSession';
import { SignupPlan, SignupPlanNotice, SignupState } from '../types';

const PLAN_NOTICES: SignupPlanNotice[] = ['expired', 'linkDead'];

/**
 * Read defensively: location state is whatever the last navigation put there — nothing at all on a reload —
 * and a value nobody recognises has to leave the screen as it would have been without it.
 */
const readNotice = (locationState: unknown): SignupPlanNotice | null => {
  const notice = (locationState as { signupNotice?: unknown } | null)?.signupNotice;
  return PLAN_NOTICES.find(known => known === notice) ?? null;
};

/** The steps a funnel can be standing on while it still lives in this browser rather than behind a login. */
export type SignupInProgressStep = 'account' | 'verify_email' | 'company_info';

const IN_PROGRESS_ROUTES: Record<SignupInProgressStep, string> = {
  account: '/signup/account',
  // The details are saved and a code is already in their inbox, so the form has nothing left to ask.
  verify_email: '/signup/verify',
  company_info: '/signup/company',
};

/**
 * What the funnel behind a stored token turns out to be, seen from the plan screen.
 *
 * `inProgress` is deliberately not a redirect. Sending the visitor straight back to the step they stopped on
 * is what made this screen a trap: none of those steps offers a way out, so the only escape from a signup
 * somebody has given up on was to clear the browser's storage. Offered beside the plans instead — and
 * choosing one of those plans is what leaves the old funnel behind, so nothing else here has to.
 *
 * Not `signupResumeRoute` from the helpers, which answers a different question: that one names the furthest
 * funnel step open to a client who is signed in, and everything this screen sees is either before the account
 * exists or already behind the login.
 */
type FunnelStanding =
  | { kind: 'inProgress'; step: SignupInProgressStep }
  | { kind: 'redirect'; route: string }
  | { kind: 'none' };

const standingOf = (state: SignupState): FunnelStanding => {
  if (state.status === 'started') {
    if (state.step === 'account') return { kind: 'inProgress', step: 'account' };
    if (state.step === 'verify_email') return { kind: 'inProgress', step: 'verify_email' };
    if (state.step === 'company_info') return { kind: 'inProgress', step: 'company_info' };
    return { kind: 'none' };
  }

  // Confirmed and beyond: the account exists, and the rest of the setup happens signed in.
  return state.status === 'expired' ? { kind: 'none' } : { kind: 'redirect', route: '/login' };
};

// `stay` and `redirect` rest on an answer and may spend the token; `unavailable` must leave it alone.
type ResumeOutcome =
  | { kind: 'stay'; inProgress: SignupInProgressStep | null }
  | { kind: 'redirect'; route: string }
  | { kind: 'unavailable' };

const PLAN_UNAVAILABLE_MESSAGE = 'This plan is not available. Please choose another one.';

export const useSignupPlanViewController = () => {
  const api = useSignupApi();
  const navigate = useNavigate();
  const location = useLocation();
  const linkedPlanId = new URLSearchParams(location.search).get('plan');

  const [plans, setPlans] = useState<SignupPlan[]>([]);
  const [localNumberPriceUsd, setLocalNumberPriceUsd] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingPlanId, setStartingPlanId] = useState<string | null>(null);
  // Why a client who had a funnel is being shown the list again.
  const [notice, setNotice] = useState<SignupPlanNotice | null>(() => readNotice(location.state));
  // The unfinished funnel this browser is holding, offered above the plans rather than jumped into.
  const [inProgress, setInProgress] = useState<SignupInProgressStep | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setUnavailable(false);
    setLoading(true);
    setAttempt(current => current + 1);
  }, []);

  const startFunnel = useCallback(async(planId: string) => {
    // Before any navigation: the attribution is read from the address bar, and leaving it loses the campaign.
    const { data } = await api.start({ planId: planId, attribution: collectAttribution() });
    setSignupToken(data.sessionToken);
    navigate('/signup/account');
  }, [api, navigate]);

  useEffect(() => {
    let active = true;

    const resume = async(): Promise<ResumeOutcome> => {
      if (getSignupToken() == null) return { kind: 'stay', inProgress: null };

      let standing: FunnelStanding = { kind: 'none' };
      try {
        const { data } = await api.getState();
        standing = standingOf(data);
        // Set here because this is the only place that reads the funnel behind a stored token.
        if (data.status === 'expired') setNotice('expired');
      } catch (e) {
        // Clearing the token on a timeout strands a signup sitting there untouched and offers this visitor a
        // second one to start beside it. Only a verdict the API actually gave spends the token.
        if (isTransientFailure(e)) return { kind: 'unavailable' };
      }

      // Worth keeping only for the public steps that still need it — the form and the code screen both
      // speak to the funnel with this token, and the code screen also resends with it. Holding on to it
      // after the funnel is finished or dead would bounce every later visit to this screen off the same
      // stale state.
      if (standing.kind !== 'inProgress') clearSignupToken();

      if (standing.kind === 'redirect') return { kind: 'redirect', route: standing.route };
      return { kind: 'stay', inProgress: standing.kind === 'inProgress' ? standing.step : null };
    };

    const load = async() => {
      const outcome = await resume();
      if (!active) return;
      if (outcome.kind === 'unavailable') {
        setUnavailable(true);
        setLoading(false);
        return;
      }
      if (outcome.kind === 'redirect') {
        // The route change unmounts this screen; leaving `loading` set keeps the spinner until it does.
        navigate(outcome.route);
        return;
      }

      setInProgress(outcome.inProgress);

      let plans: SignupPlan[] = [];
      try {
        const { data } = await api.getPlans();
        if (!active) return;
        plans = data.plans;
        setPlans(data.plans);
        setLocalNumberPriceUsd(data.localNumberPriceUsd);
      } catch (e) {
        if (!active) return;
        // A disabled application answers exactly like one that has no funnel: there is nothing to sign up
        // to here, so the visitor belongs on the login screen.
        if (errorStatus(e) === 404) {
          navigate('/login');
          return;
        }
        setError(errorMessage(e, 'Plans could not be loaded. Please try again.'));
        setLoading(false);
        return;
      }

      // A funnel already in this browser wins over the link, which would otherwise start a second one beside it.
      const linked = outcome.inProgress == null ? plans.find(plan => plan.id === linkedPlanId) : undefined;
      if (linked != null) {
        try {
          // `loading` stays set: the route change unmounts this screen, and the cards never show on the way.
          await startFunnel(linked.id);
          return;
        } catch (e) {
          if (!active) return;
          setError(errorMessage(e, PLAN_UNAVAILABLE_MESSAGE));
        }
      }

      setLoading(false);
    };

    load();

    return () => {
      active = false;
    };
  }, [api, navigate, attempt, linkedPlanId, startFunnel]);

  const choosePlan = useCallback(
    async(planId: string) => {
      setStartingPlanId(planId);
      setError(null);
      try {
        await startFunnel(planId);
      } catch (e) {
        setError(errorMessage(e, PLAN_UNAVAILABLE_MESSAGE));
      } finally {
        setStartingPlanId(null);
      }
    },
    [startFunnel],
  );

  const continueSignup = useCallback(() => {
    if (inProgress == null) return;
    navigate(IN_PROGRESS_ROUTES[inProgress]);
  }, [inProgress, navigate]);

  return {
    plans,
    localNumberPriceUsd,
    loading,
    error,
    notice,
    inProgress,
    startingPlanId,
    unavailable,
    choosePlan,
    continueSignup,
    retry,
  };
};
